import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Ship,
  Star,
  Mail,
  User,
  ArrowUpRight,
  Sparkles,
  Pencil,
  Check,
  X,
  Anchor,
  Activity,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatMoney, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const imageSrc = (path) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return `${process.env.REACT_APP_BACKEND_URL}/api/files/${path}`;
};

// Reusable tile shell — colourful panel with a header strip + scrollable body.
// `tone` picks the accent stripe; everything else stays on the SOS dark base.
function DashboardTile({
  title,
  count,
  icon: Icon,
  tone = "blue",
  action,
  children,
  testid,
}) {
  const tones = {
    blue: {
      stripe: "bg-gradient-to-r from-[#0088CC] via-[#0088CC]/80 to-[#0d4863]",
      accent: "text-[#0088CC]",
      ring: "ring-[#0088CC]/30",
    },
    deep: {
      stripe: "bg-gradient-to-r from-[#0d3a52] via-[#0d2336] to-[#080a0c]",
      accent: "text-[#0088CC]",
      ring: "ring-[#0088CC]/20",
    },
    teal: {
      stripe: "bg-gradient-to-r from-[#1c6a8a] via-[#1a4d66] to-[#0b1d28]",
      accent: "text-[#5BD0EE]",
      ring: "ring-[#0088CC]/30",
    },
    slate: {
      stripe: "bg-gradient-to-r from-[#243341] via-[#1c2734] to-[#0c1219]",
      accent: "text-[#9FB2C2]",
      ring: "ring-white/10",
    },
  }[tone];

  return (
    <section
      className={`sos-surface rounded-2xl overflow-hidden flex flex-col ring-1 ${tones.ring}`}
      data-testid={testid}
    >
      <div className={`${tones.stripe} px-5 py-3 flex items-center justify-between gap-3`}>
        <div className="flex items-center gap-2.5 text-white min-w-0">
          <Icon className="h-4 w-4 shrink-0" />
          <div className="font-display uppercase tracking-[0.18em] text-xs sm:text-sm font-semibold truncate">
            {title}
          </div>
          {count != null && (
            <span className="ml-1 text-[10px] tracking-wider font-display font-semibold px-1.5 py-0.5 rounded-full bg-white/25 text-white shrink-0">
              {count}
            </span>
          )}
        </div>
        {action}
      </div>
      <div className="flex-1 p-4 sm:p-5">{children}</div>
    </section>
  );
}

// Vessel-row used in New / Favourites tiles. Compact horizontal layout so a
// handful fit without each tile needing its own scrollbar.
function VesselRow({ vessel, onUnfavourite }) {
  const flyerHref = `/vessels/${vessel.id}/flyer`;
  return (
    <div
      className="group flex items-center gap-3 p-2 rounded-lg hover:bg-white transition-colors"
      data-testid={`dashboard-vessel-${vessel.id}`}
    >
      <Link
        to={flyerHref}
        className="w-16 h-12 bg-white rounded overflow-hidden shrink-0 relative"
        data-testid={`dashboard-vessel-image-${vessel.id}`}
      >
        {vessel.render_path ? (
          <img
            src={imageSrc(vessel.render_path)}
            alt={vessel.name}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Ship className="h-5 w-5 text-[#B6C2CD]" />
          </div>
        )}
      </Link>
      <Link
        to={flyerHref}
        className="min-w-0 flex-1 block group/name"
        data-testid={`dashboard-vessel-name-${vessel.id}`}
      >
        <div className="text-sm font-display font-semibold text-[#011836] truncate group-hover/name:text-[#0088CC] transition-colors">
          {vessel.name}
        </div>
        <div className="text-[11px] uppercase tracking-wider text-[#5B6B7C] truncate">
          {vessel.sub_category || vessel.vessel_type || "Vessel"}
          {vessel.price != null && (
            <>
              <span className="mx-1.5 text-[#475569]">·</span>
              <span className="text-[#0088CC] font-semibold">
                {formatMoney(vessel.price, vessel.price_currency)}
              </span>
            </>
          )}
        </div>
      </Link>
      {onUnfavourite && (
        <button
          type="button"
          onClick={() => onUnfavourite(vessel)}
          className="p-1.5 rounded text-amber-700 hover:text-amber-200 hover:bg-amber-500/15 opacity-60 group-hover:opacity-100"
          data-testid={`dashboard-unfav-${vessel.id}`}
          title="Remove from favourites"
        >
          <Star className="h-3.5 w-3.5 fill-amber-300" />
        </button>
      )}
    </div>
  );
}

function EmptyHint({ message, cta }) {
  return (
    <div className="text-center py-8">
      <div className="text-sm text-[#5B6B7C]">{message}</div>
      {cta && <div className="mt-3">{cta}</div>}
    </div>
  );
}

export default function ClientDashboard() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: "", company: "", phone: "" });
  const [savingProfile, setSavingProfile] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/client/dashboard");
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startEditProfile = () => {
    setProfileForm({
      name: data?.profile?.name || "",
      company: data?.profile?.company || "",
      phone: data?.profile?.phone || "",
    });
    setEditingProfile(true);
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.patch("/client/profile", profileForm);
      await refresh();
      await load();
      setEditingProfile(false);
      toast.success("Profile updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const unfavourite = async (vessel) => {
    try {
      await api.post(`/client/favourites/${vessel.id}`);
      setData((d) => ({
        ...d,
        favourite_vessels: d.favourite_vessels.filter((v) => v.id !== vessel.id),
      }));
      toast.success(`Removed ${vessel.name} from favourites`);
    } catch {
      toast.error("Could not update favourite");
    }
  };

  const greetingName = (user?.name || user?.email || "").split(" ")[0] || "there";
  const prevLogin = data?.previous_login_at && user?.previous_login_at;

  return (
    <div className="p-4 sm:p-8 lg:p-10 max-w-[1400px] mx-auto pb-24" data-testid="client-dashboard">
      {/* Hero band — welcome + primary CTA, intentionally bold per the
          reference mock the user shared. */}
      <div className="rounded-2xl sm:rounded-3xl overflow-hidden relative mb-6 sm:mb-8 bg-gradient-to-br from-[#0d3a52] via-[#0b2638] to-[#080a0c] border border-[#0088CC]/20">
        <div
          className="absolute inset-0 opacity-25 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 12% 18%, rgba(30,156,215,0.45) 0%, transparent 45%), radial-gradient(circle at 85% 85%, rgba(63,182,230,0.25) 0%, transparent 50%)",
          }}
          aria-hidden
        />
        <div className="relative px-5 sm:px-10 py-8 sm:py-14 grid sm:grid-cols-[1fr_auto] items-end gap-5 sm:gap-6">
          <div>
            <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.32em] text-[#5BC4F0] mb-2 sm:mb-3 flex items-center gap-2">
              <Anchor className="h-3 w-3" />
              Welcome back
            </div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
              {greetingName}
            </h1>
            <p className="mt-2 sm:mt-3 text-sm sm:text-base text-[#B9CBDA] max-w-xl">
              {prevLogin
                ? `Last signed in ${formatDate(user.previous_login_at)} — anything new in the catalogue since then is in the New Vessels tile below.`
                : "Browse our live vessel catalogue, save the models you're interested in, and request a quote in a click."}
            </p>
          </div>
          <Link
            to="/vessels"
            className="inline-flex items-center justify-center gap-2 h-12 sm:h-auto px-5 sm:py-3 rounded-full bg-[#0088CC] hover:bg-[#006DA6] text-white font-display tracking-wide text-sm transition-colors active:scale-[0.97] w-full sm:w-auto"
            data-testid="dashboard-browse-cta"
          >
            <Ship className="h-4 w-4" />
            Browse catalogue
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20 text-sm text-[#5B6B7C]">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6">
          {/* NEW VESSELS */}
          <DashboardTile
            title="New Vessels"
            icon={Sparkles}
            tone="blue"
            count={data?.new_vessels?.length || 0}
            testid="tile-new-vessels"
            action={
              <Link
                to="/vessels"
                className="text-[10px] uppercase tracking-wider text-white/80 hover:text-white inline-flex items-center gap-1 font-display font-semibold"
              >
                See all <ArrowUpRight className="h-3 w-3" />
              </Link>
            }
          >
            {(data?.new_vessels || []).length === 0 ? (
              <EmptyHint
                message={
                  prevLogin
                    ? "Nothing new since your last visit. We'll let you know when fresh designs land."
                    : "No new vessels in the last 30 days — check the catalogue for our complete fleet."
                }
                cta={
                  <Link
                    to="/vessels"
                    className="inline-flex items-center gap-2 text-xs text-[#0088CC] hover:underline"
                  >
                    Open catalogue <ArrowUpRight className="h-3 w-3" />
                  </Link>
                }
              />
            ) : (
              <div className="space-y-1">
                {data.new_vessels.slice(0, 6).map((v) => (
                  <VesselRow key={v.id} vessel={v} />
                ))}
              </div>
            )}
          </DashboardTile>

          {/* FAVOURITES */}
          <DashboardTile
            title="Favourite Vessels"
            icon={Star}
            tone="teal"
            count={data?.favourite_vessels?.length || 0}
            testid="tile-favourites"
            action={
              <Link
                to="/vessels"
                className="text-[10px] uppercase tracking-wider text-white/80 hover:text-white inline-flex items-center gap-1 font-display font-semibold"
              >
                Browse <ArrowUpRight className="h-3 w-3" />
              </Link>
            }
          >
            {(data?.favourite_vessels || []).length === 0 ? (
              <EmptyHint
                message="No favourites yet. Tap the star on any vessel card to bookmark it here."
              />
            ) : (
              <div className="space-y-1">
                {data.favourite_vessels.map((v) => (
                  <VesselRow key={v.id} vessel={v} onUnfavourite={unfavourite} />
                ))}
              </div>
            )}
          </DashboardTile>

          {/* QUOTES */}
          <DashboardTile
            title="Quotes"
            icon={Mail}
            tone="deep"
            count={data?.recent_quote_requests?.length || 0}
            testid="tile-quotes"
          >
            {(data?.recent_quote_requests || []).length === 0 ? (
              <EmptyHint
                message="You haven't requested any quotes yet."
                cta={
                  <Link
                    to="/vessels"
                    className="inline-flex items-center gap-2 text-xs text-[#0088CC] hover:underline"
                  >
                    Find a vessel to enquire about <ArrowUpRight className="h-3 w-3" />
                  </Link>
                }
              />
            ) : (
              <div className="space-y-2.5">
                {data.recent_quote_requests.slice(0, 8).map((r) => (
                  <div
                    key={r.id}
                    className="rounded-lg border border-[#0088CC]/15 bg-white p-3"
                    data-testid={`quote-request-${r.id}`}
                  >
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="font-display font-semibold text-sm text-[#011836] truncate">
                        {r.vessel_name}
                      </div>
                      <span
                        className={`text-[9px] uppercase tracking-wider font-display font-semibold px-1.5 py-0.5 rounded ${
                          r.status === "new"
                            ? "bg-amber-500/20 text-amber-700 border border-amber-400/40"
                            : r.status === "read"
                              ? "bg-[#0088CC]/20 text-[#0088CC] border border-[#0088CC]/40"
                              : "bg-[#E2E8F0] text-[#5B6B7C] border border-[#C4D0DB]"
                        }`}
                      >
                        {r.status === "new" ? "Awaiting reply" : r.status === "read" ? "Seen by SOS" : "Archived"}
                      </span>
                    </div>
                    <div className="text-[11px] text-[#5B6B7C] mt-1">
                      Sent {formatDate(r.created_at)}
                    </div>
                    {r.message && (
                      <div className="text-xs text-[#33475C] mt-2 line-clamp-2">
                        {r.message}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </DashboardTile>

          {/* PROFILE */}
          <DashboardTile
            title="Profile"
            icon={User}
            tone="slate"
            testid="tile-profile"
            action={
              !editingProfile && (
                <button
                  type="button"
                  onClick={startEditProfile}
                  className="text-[10px] uppercase tracking-wider text-white/80 hover:text-white inline-flex items-center gap-1 font-display font-semibold"
                  data-testid="edit-profile-btn"
                >
                  <Pencil className="h-3 w-3" /> Edit
                </button>
              )
            }
          >
            {editingProfile ? (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-wider text-[#5B6B7C]">
                    Full name
                  </Label>
                  <Input
                    value={profileForm.name}
                    onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                    className="bg-white border-[#DDE5EC] text-[#011836]"
                    data-testid="profile-name-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-wider text-[#5B6B7C]">
                    Company
                  </Label>
                  <Input
                    value={profileForm.company}
                    onChange={(e) => setProfileForm({ ...profileForm, company: e.target.value })}
                    className="bg-white border-[#DDE5EC] text-[#011836]"
                    data-testid="profile-company-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-wider text-[#5B6B7C]">
                    Phone
                  </Label>
                  <Input
                    value={profileForm.phone}
                    onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                    className="bg-white border-[#DDE5EC] text-[#011836]"
                    data-testid="profile-phone-input"
                  />
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <Button
                    type="button"
                    size="sm"
                    onClick={saveProfile}
                    disabled={savingProfile}
                    className="bg-[#0088CC] hover:bg-[#006DA6] text-white"
                    data-testid="profile-save-btn"
                  >
                    <Check className="h-3.5 w-3.5 mr-1" /> {savingProfile ? "Saving…" : "Save"}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setEditingProfile(false)}
                    disabled={savingProfile}
                    className="border-[#C4D0DB] text-[#5B6B7C] hover:text-[#011836]"
                    data-testid="profile-cancel-btn"
                  >
                    <X className="h-3.5 w-3.5 mr-1" /> Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#5B6B7C]">
                    Full name
                  </div>
                  <div className="text-[#011836] mt-0.5" data-testid="profile-name">
                    {data?.profile?.name || "—"}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#5B6B7C]">
                    Company
                  </div>
                  <div className="text-[#011836] mt-0.5" data-testid="profile-company">
                    {data?.profile?.company || "—"}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#5B6B7C]">
                      Email
                    </div>
                    <div className="text-[#011836] mt-0.5 truncate" data-testid="profile-email">
                      {data?.profile?.email}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#5B6B7C]">
                      Phone
                    </div>
                    <div className="text-[#011836] mt-0.5" data-testid="profile-phone">
                      {data?.profile?.phone || "—"}
                    </div>
                  </div>
                </div>
                <div className="pt-2 mt-2 border-t border-[#DDE5EC] text-[11px] text-[#64748B]">
                  Member since {formatDate(data?.profile?.created_at)}
                </div>
              </div>
            )}
          </DashboardTile>
        </div>
      )}

      {/* "What's new" — chronological activity timeline. Sits below the
          tiles so it's secondary content for returning visitors who want
          to see fine-grained catalogue changes (not just brand-new vessels). */}
      {!loading && (data?.activity || []).length > 0 && (
        <section
          className="sos-surface rounded-2xl overflow-hidden mt-6 ring-1 ring-[#0088CC]/10"
          data-testid="tile-activity"
        >
          <div className="bg-gradient-to-r from-[#1c3145] via-[#162635] to-[#0a1118] px-5 py-3 flex items-center gap-2.5 text-white">
            <Activity className="h-4 w-4" />
            <div className="font-display uppercase tracking-[0.18em] text-xs sm:text-sm font-semibold">
              What&rsquo;s new in the catalogue
            </div>
          </div>
          <ol className="relative p-5 sm:p-6 space-y-4">
            {data.activity.map((a, idx) => (
              <li
                key={a.id}
                className="relative pl-6"
                data-testid={`activity-${a.id}`}
              >
                {/* Dot + connector line */}
                <span
                  className="absolute left-0 top-1.5 h-2.5 w-2.5 rounded-full bg-[#0088CC] ring-4 ring-[#0088CC]/15"
                  aria-hidden
                />
                {idx !== data.activity.length - 1 && (
                  <span
                    className="absolute left-[5px] top-5 bottom-[-1rem] w-px bg-[#E2E8F0]"
                    aria-hidden
                  />
                )}
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-display text-sm font-semibold text-[#011836]">
                    {a.vessel_name}
                  </span>
                  <span className="text-[#5B6B7C]">·</span>
                  <span className="text-sm text-[#0088CC]">{a.action}</span>
                </div>
                <div className="mt-0.5 text-[11px] uppercase tracking-wider text-[#64748B]">
                  {formatDate(a.created_at)}
                </div>
                {a.detail && (
                  <div className="text-xs text-[#33475C] mt-1">{a.detail}</div>
                )}
              </li>
            ))}
          </ol>
        </section>
      )}
    </div>
  );
}
